import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './demo.css';

/* ═══════════════════════════════════════════════════════
   TIMELINE
   ═══════════════════════════════════════════════════════ */
const TOTAL = 62;

const SC = {
  HOOK:         { s: 0,    e: 7,    fi: 0 },
  CATEGORY:     { s: 7,    e: 16,   fi: 0.4 },
  SCALE:        { s: 16,   e: 24,   fi: 0.4 },
  HERO:         { s: 24,   e: 34,   fi: 0.4 },
  DEFENSIBILITY:{ s: 34,   e: 42,   fi: 0.4 },
  CSSQUERY:     { s: 42,   e: 48,   fi: 0.4 },
  TIMING:       { s: 48,   e: 55,   fi: 0.4 },
  CLOSE:        { s: 55,   e: 62,   fi: 0 },
};

function useTimeline(duration, onEnd) {
  const [t, setT] = useState(0);
  const r = useRef({ start: null, raf: null, done: false });
  useEffect(() => {
    const state = r.current;
    state.start = performance.now();
    const tick = () => {
      const el = (performance.now() - state.start) / 1000;
      setT(el);
      if (el >= duration) {
        if (!state.done) { state.done = true; onEnd?.(); }
        return;
      }
      state.raf = requestAnimationFrame(tick);
    };
    state.raf = requestAnimationFrame(tick);
    return () => { if (state.raf) cancelAnimationFrame(state.raf); };
  }, [duration, onEnd]);
  return t;
}

/* ═══════════════════════════════════════════════════════
   VISUAL PANELS
   ═══════════════════════════════════════════════════════ */

function SIEMPanel() {
  return (
    <div style={{ background: '#0A0A0F', height: '100%', padding: 20 }}>
      {/* Alert list */}
      {[
        { sev: '#EF4444', text: 'CRITICAL: Unauthorized access attempt detected' },
        { sev: '#EF4444', text: 'HIGH: Malware signature match — endpoint 14' },
        { sev: '#F59E0B', text: 'MEDIUM: Anomalous login pattern — APAC region' },
        { sev: '#F59E0B', text: 'MEDIUM: Port scan detected — DMZ segment' },
        { sev: '#10B981', text: 'LOW: Certificate expiry warning — 14 days' },
        { sev: '#10B981', text: 'LOW: Routine firewall rule update applied' },
      ].map((a, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #1E2A4530' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: a.sev, flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: '#94A3B8' }}>{a.text}</span>
        </div>
      ))}
      <div style={{ marginTop: 20 }}>
        <div style={{ height: 5, width: '60%', background: '#EF4444', borderRadius: 3, opacity: 0.4, marginBottom: 8 }} />
        <div style={{ height: 5, width: '40%', background: '#F59E0B', borderRadius: 3, opacity: 0.4, marginBottom: 8 }} />
        <div style={{ height: 5, width: '80%', background: '#10B981', borderRadius: 3, opacity: 0.4 }} />
      </div>
    </div>
  );
}

function GRCPanel() {
  const rows = [
    { ctrl: 'AC-2', name: 'Account Management', status: 'covered' },
    { ctrl: 'AU-2', name: 'Audit Events', status: 'covered' },
    { ctrl: 'CM-3', name: 'Configuration Change Control', status: 'partial' },
    { ctrl: 'IR-4', name: 'Incident Handling', status: 'covered' },
    { ctrl: 'RA-3', name: 'Risk Assessment', status: 'partial' },
    { ctrl: 'SC-7', name: 'Boundary Protection', status: 'covered' },
  ];
  return (
    <div style={{ background: '#0A0A0F', height: '100%', padding: 20 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', fontSize: 9, color: '#475569', padding: '6px 4px', borderBottom: '1px solid #1E2A45' }}>Control</th>
            <th style={{ textAlign: 'left', fontSize: 9, color: '#475569', padding: '6px 4px', borderBottom: '1px solid #1E2A45' }}>Name</th>
            <th style={{ textAlign: 'left', fontSize: 9, color: '#475569', padding: '6px 4px', borderBottom: '1px solid #1E2A45' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.ctrl}>
              <td style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: '#94A3B8', padding: '6px 4px', borderBottom: '1px solid #1E2A4530' }}>{r.ctrl}</td>
              <td style={{ fontSize: 11, color: '#94A3B8', padding: '6px 4px', borderBottom: '1px solid #1E2A4530' }}>{r.name}</td>
              <td style={{ padding: '6px 4px', borderBottom: '1px solid #1E2A4530' }}>
                <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: r.status === 'covered' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: r.status === 'covered' ? '#10B981' : '#F59E0B' }}>
                  {r.status === 'covered' ? 'Covered' : 'Partial'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AIObsPanel() {
  return (
    <div style={{ background: '#0A0A0F', height: '100%', padding: 20 }}>
      {/* Fake metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Accuracy', val: '94.2%' },
          { label: 'Latency P99', val: '340ms' },
          { label: 'Drift Score', val: '0.12' },
          { label: 'Throughput', val: '2.4K/hr' },
        ].map(m => (
          <div key={m.label} style={{ padding: 10, borderRadius: 8, background: 'rgba(15,22,41,0.6)', border: '1px solid #1E2A4550' }}>
            <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#F8FAFC' }}>{m.val}</div>
          </div>
        ))}
      </div>
      {/* Fake drift chart */}
      <svg width="100%" height="80" viewBox="0 0 300 80" preserveAspectRatio="none" style={{ opacity: 0.6 }}>
        <path d="M0,60 C30,55 60,50 90,52 C120,54 150,40 180,35 C210,30 240,45 270,50 L300,48" fill="none" stroke="#F59E0B" strokeWidth="1.5" />
        <path d="M0,40 C30,38 60,42 90,38 C120,34 150,30 180,28 C210,26 240,30 270,32 L300,30" fill="none" stroke="#6366F1" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

/* Scene 4 — Clean DER Hero */
function CleanDERBG() {
  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '80%', maxWidth: 1000 }}>
        <div style={{ background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderTop: '3px solid #6366F1', borderRadius: 12, padding: '32px 40px' }}>
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 28, fontWeight: 700, color: '#F8FAFC', marginBottom: 12 }}>
              DER-2026-047832
            </div>
            <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", padding: '4px 12px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981' }}>
              EVIDENCE COMPLETE · NO VIOLATIONS DETECTED
            </span>
          </div>

          {/* Signature */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 11, color: '#475569' }}>Signature:</span>
            <code style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#94A3B8', background: '#080D1A', padding: '4px 10px', borderRadius: 6 }}>
              sha256:9f3a2c8e7b1d4f5a6c0e8d2b7f4a1c3e...
            </code>
          </div>

          <div style={{ fontSize: 11, color: '#64748B', marginBottom: 24 }}>April 15, 2026 · 14:32:07 UTC · Zurich, Switzerland</div>

          {/* Risk badges */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24, paddingTop: 16, borderTop: '1px solid #1E2A45' }}>
            {[
              { text: 'Risk Level: HIGH', color: '#EF4444' },
              { text: 'Decision Class: Regulated workflow · EU AI Act Annex III', color: '#F59E0B' },
              { text: 'Audit Relevance: SOC 2 sample eligible · EU AI Act audit scope', color: '#6366F1' },
            ].map(b => (
              <span key={b.text} style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", padding: '4px 10px', borderRadius: 20, background: b.color + '10', border: `1px solid ${b.color}33`, color: b.color }}>{b.text}</span>
            ))}
          </div>

          {/* Decision context */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Model', value: 'GPT-4o 2025-11' },
              { label: 'Workflow', value: 'Contract Classification' },
              { label: 'Principal', value: 'svc-contract-ai@meridian' },
              { label: 'Output Type', value: 'Classification + confidence' },
            ].map(f => (
              <div key={f.label}>
                <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{f.label}</div>
                <div style={{ fontSize: 12, color: '#94A3B8' }}>{f.value}</div>
              </div>
            ))}
          </div>

          {/* Framework badges */}
          <div style={{ display: 'flex', gap: 8, marginTop: 24, paddingTop: 16, borderTop: '1px solid #1E2A45' }}>
            {['EU AI Act 9/9', 'ISO 42001 8/8', 'NIST AI RMF 10/10', 'SOC 2 PI1.1·CC6.1'].map(fw => (
              <span key={fw} style={{ fontSize: 10, padding: '4px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981' }}>{fw}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ANIMATED COUNTER — smooth count-up between stages
   ═══════════════════════════════════════════════════════ */
function AnimatedCounter({ t, sceneStart }) {
  const elapsed = t - sceneStart;
  if (elapsed < 0) return null;

  const segments = [
    { from: 1000, to: 1000, cStart: 0, cEnd: 0, holdEnd: 0.8 },
    { from: 1000, to: 10000, cStart: 0.8, cEnd: 1.5, holdEnd: 2.3 },
    { from: 10000, to: 47832, cStart: 2.3, cEnd: 3.2, holdEnd: 4.0 },
    { from: 47832, to: 100000, cStart: 4.0, cEnd: 4.8, holdEnd: 5.6 },
  ];

  let value = 1000;
  for (const seg of segments) {
    if (elapsed < seg.cStart) break;
    if (elapsed < seg.cEnd) {
      const p = (elapsed - seg.cStart) / (seg.cEnd - seg.cStart);
      const ep = 1 - Math.pow(1 - p, 3); // ease-out cubic
      value = Math.round(seg.from + (seg.to - seg.from) * ep);
      break;
    }
    value = seg.to;
  }

  const formatted = value >= 100000 ? '100,000+' : value.toLocaleString();

  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 72, fontWeight: 700, color: '#F8FAFC', textAlign: 'center', letterSpacing: 2 }}>
      {formatted}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function InvestorDemo() {
  const nav = useNavigate();
  const onEnd = useCallback(() => nav('/demo'), [nav]);
  const t = useTimeline(TOTAL, onEnd);

  const fade = (at, d = 0.5) => Math.min(1, Math.max(0, (t - at) / d));
  const prog = (a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));

  const lOp = (sc) => {
    if (sc.fi === 0) return t >= sc.s && t < sc.e ? 1 : 0;
    const fs = sc.s - sc.fi;
    if (t < fs) return 0;
    if (t < sc.s) return (t - fs) / sc.fi;
    if (t < sc.e) return 1;
    return 0;
  };

  /* Close text fade */
  const closeTextOp = (() => {
    if (t < 59) return 1;
    if (t < 61) return 1 - (t - 59) / 2;
    return 0;
  })();

  /* Panel blur for Scene 3 quick cuts */
  const scaleBlur = (() => {
    const el = t - SC.SCALE.s;
    if (el < 0) return { panel: 0, blur: 3 };
    const cut = Math.floor(el / 1.5);
    return { panel: Math.min(cut, 2), blur: 3 + cut * 1.5 };
  })();

  return (
    <div className="demo-root" data-testid="investor-demo">

      {/* ═══ S1 — HOOK (0-7) ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.HOOK), zIndex: 1 }}>
        <div className="demo-center-stack">
          <p className="demo-hero" style={{ opacity: fade(1.0, 0.6) }}>
            AI is making decisions inside every enterprise.
          </p>
          <p className="demo-hero" style={{ opacity: fade(2.3, 0.6), marginTop: 24 }}>
            But almost no one records what actually happened.
          </p>
        </div>
      </div>

      {/* ═══ S2 — CATEGORY INSIGHT (7-16) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.CATEGORY), zIndex: 2 }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, width: '100%', maxWidth: 1400 }}>
            {/* SIEM Panel */}
            <div style={{ opacity: 0.6, filter: 'blur(3px)', borderRadius: 12, overflow: 'hidden', border: '1px solid #1E2A4540', height: 360 }}>
              <SIEMPanel />
            </div>
            {/* GRC Panel */}
            <div style={{ opacity: 0.6, filter: 'blur(3px)', borderRadius: 12, overflow: 'hidden', border: '1px solid #1E2A4540', height: 360 }}>
              <GRCPanel />
            </div>
            {/* AI Observability Panel */}
            <div style={{ opacity: 0.6, filter: 'blur(3px)', borderRadius: 12, overflow: 'hidden', border: '1px solid #1E2A4540', height: 360 }}>
              <AIObsPanel />
            </div>
          </div>
        </div>
        {/* Text overlay */}
        <div className="demo-overlay-bottom">
          <p className="demo-text-overlay" style={{ opacity: fade(9.0) }}>These systems exist…</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(9.5) }}>But none of them prove the governance chain — only current posture.</p>
        </div>
      </div>

      {/* ═══ S3 — PROBLEM SCALE (16-24) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.SCALE), zIndex: 3 }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, width: '100%', maxWidth: 1400 }}>
            {[SIEMPanel, GRCPanel, AIObsPanel].map((Panel, i) => (
              <div key={i} style={{
                opacity: scaleBlur.panel === i ? 0.5 : 0.2,
                filter: `blur(${scaleBlur.blur}px)`,
                borderRadius: 12, overflow: 'hidden',
                border: '1px solid #1E2A4540', height: 360,
                transition: 'opacity 0.3s, filter 0.5s',
              }}>
                <Panel />
              </div>
            ))}
          </div>
        </div>
        <div className="demo-overlay-bottom">
          <p className="demo-text-overlay" style={{ opacity: fade(16.5) }}>So when something goes wrong…</p>
          <p className="demo-text-overlay" style={{ opacity: fade(17.0) }}>Evidence is reconstructed manually.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(17.8) }}>Slow.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(18.4) }}>Expensive.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(19.0) }}>Incomplete.</p>
        </div>
      </div>

      {/* ═══ S4 — PRODUCT HERO (24-35) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.HERO), zIndex: 4 }}>
        <div className="demo-bg-fill" style={{
          transform: `scale(${1 + prog(SC.HERO.s, SC.HERO.e) * 0.05})`,
          transformOrigin: '50% 45%',
        }}>
          <CleanDERBG />
        </div>
        <div className="demo-overlay-bottom-left">
          <p className="demo-text-overlay" style={{ opacity: fade(25.5) }}>Apex creates the missing layer.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(26.1) }}>Control State at Decision Time — the exact governance environment that governed every decision, reconstructed at any point in history.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(26.9) }}>Signed.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(27.3) }}>Immutable.</p>
        </div>
      </div>

      {/* ═══ S5 — DEFENSIBILITY (35-44) ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.DEFENSIBILITY), zIndex: 5, flexDirection: 'column' }}>
        <div style={{ opacity: fade(35.5, 0.4) }}>
          <AnimatedCounter t={t} sceneStart={35.5} />
        </div>
        <div style={{ marginTop: 48, textAlign: 'center', maxWidth: 700 }}>
          <p className="demo-text-overlay" style={{ opacity: fade(41.5) }}>Evidence accumulates.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(39.0) }}>Evidence accumulates.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(39.5) }}>Decision history compounds.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(40.3) }}>The longer it runs…</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(40.8) }}>The harder it is to replace.</p>
        </div>
      </div>

      {/* ═══ S5b — CSS GAP QUERY (42-48) ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.CSSQUERY), zIndex: 6, flexDirection: 'column' }}>
        <div style={{ width: '80%', maxWidth: 900 }}>
          <div style={{ background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderRadius: 12, padding: '20px 28px', marginBottom: 24 }}>
            <div style={{ fontSize: 9, color: '#6366F1', fontFamily: "'IBM Plex Mono', monospace", marginBottom: 8 }}>Temporal governance query · Proof Graph Service</div>
            <div style={{ fontSize: 14, color: '#F8FAFC', opacity: fade(43.0) }}>Show every HIGH-RISK decision executed while Control State had an active gap</div>
          </div>
          <div style={{ opacity: fade(44.5), background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderRadius: 12, padding: '16px 24px' }}>
            <div style={{ fontSize: 12, color: '#10B981', fontFamily: "'IBM Plex Mono', monospace", marginBottom: 8 }}>4 records matched · CSS gap window: 3h 17m</div>
            <div style={{ height: 12, background: '#1E2A45', borderRadius: 6, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: '6%', width: '82%', background: 'rgba(245,158,11,0.3)', borderRadius: 4 }} />
            </div>
            <div style={{ fontSize: 9, color: '#475569', display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span>11:00</span><span style={{ color: '#F59E0B' }}>Gap: 11:14 → 14:31</span><span>15:00</span>
            </div>
          </div>
        </div>
        <div className="demo-overlay-bottom" style={{ bottom: '8%' }}>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(46.0) }}>Governance intelligence — not just governance evidence.</p>
        </div>
      </div>

      {/* ═══ S6 — MARKET TIMING ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.TIMING), zIndex: 7, flexDirection: 'column' }}>
        {/* Tiles */}
        <div style={{ display: 'flex', gap: 24, marginBottom: 48 }}>
          {[
            { text: 'EU AI Act', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)', appear: 49.0 },
            { text: 'SOC 2', color: '#818CF8', bg: 'rgba(99,102,241,0.15)', appear: 51.0 },
            { text: 'Insurance', color: '#F87171', bg: 'rgba(239,68,68,0.15)', appear: 53.0 },
          ].map((tile) => (
            <div
              key={tile.text}
              style={{
                opacity: fade(tile.appear, 0.4),
                padding: '20px 40px',
                borderRadius: 12,
                background: tile.bg,
                border: `1px solid ${tile.color}40`,
                transform: `scale(${t >= tile.appear && t < tile.appear + 0.5 ? 1.05 : 1})`,
                transition: 'transform 0.3s ease',
              }}
            >
              <span style={{ fontSize: 20, fontWeight: 600, color: tile.color }}>{tile.text}</span>
            </div>
          ))}
        </div>

        {/* Text */}
        <div style={{ textAlign: 'center', maxWidth: 700 }}>
          <p className="demo-text-overlay" style={{ opacity: fade(53.5) }}>Regulation is catching up.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(53.9) }}>Auditors are adapting.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(54.3) }}>Insurance is tightening.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(55.1) }}>The evidence layer becomes inevitable.</p>
        </div>
      </div>

      {/* ═══ S7 — CLOSE (53-60) ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.CLOSE), zIndex: 8 }}>
        <div className="demo-center-stack" style={{ opacity: closeTextOp }}>
          <p className="demo-hero-large" style={{ opacity: fade(55.5, 0.6), maxWidth: 1000, fontSize: '1.6rem' }}>
            Apex Trust creates the governance proof chain that proves what your AI was, what controlled it, what it decided, and how it was reviewed — at any point in time.
          </p>
          <p className="demo-hero-large" style={{ opacity: fade(57.0, 0.5), marginTop: 16, fontSize: '1.3rem', color: '#6366F1' }}>
            Temporal AI Governance Proof Infrastructure.
          </p>
          <p className="demo-hero-large" style={{ opacity: fade(56.0, 0.5), marginTop: 16, fontSize: '1.4rem' }}>
            First proof chain in seven days.
          </p>
        </div>
      </div>
    </div>
  );
}
